import { isSettingLookCode, type SettingLookCode } from '@gk/contracts'
import { lookForChoice, type TaskTypeRow } from './plan.ts'

/** M25-D (docs/M25.md B and E, rules 4 and 9): « FAIT » BESIDE « PRÉVU » on the job page and on an area's history — pure, tested in settingsCompare.test.ts.
 *  What was confirmed at a finish (`ops.job_settings_confirmation`) read in the order the type DECLARES its settings, each word through the type's CURRENT
 *  pictures while it is still a choice (a picture corrected in Types de travail corrects the history), and through the picture stored with the confirmation only
 *  for a word that is no longer a choice (it keeps the meaning it had; never a guess). `differs` is a word that is not the plan's on a setting the plan had
 *  (information about the turf, never a fault: nothing is flagged); a setting the plan did not have cannot differ. */
export interface DoneConfirmation { values: Readonly<Record<string, string | number>>; looks: Readonly<Record<string, string | null>> }
export interface DoneLine {
  key: string
  label: string
  value: string | number
  unit: string | null
  look: SettingLookCode | null
  differs: boolean
  /** the word is no longer a choice of the type (the page says « (n'est plus un choix) ») */
  stale: boolean
}

export function doneLines(params: Readonly<Record<string, string | number>>, tt: TaskTypeRow | undefined, conf: DoneConfirmation, lang: string): DoneLine[] {
  const l = lang.startsWith('en') ? 'en' : 'fr'
  const defs = tt?.param_defs ?? []
  const keys = [...defs.map((d) => d.key).filter((k) => Object.hasOwn(conf.values, k)), ...Object.keys(conf.values).filter((k) => !defs.some((d) => d.key === k))]
  return keys.map((key): DoneLine => {
    const def = defs.find((d) => d.key === key)
    const value = conf.values[key]!
    const current = def?.kind === 'choice' && typeof value === 'string' && !!def.choices?.includes(value)
    const stored = Object.hasOwn(conf.looks, key) ? conf.looks[key] : null
    const look = current ? lookForChoice(tt, key, value) : isSettingLookCode(stored) ? stored : null
    const planned = Object.hasOwn(params, key) ? params[key] : undefined
    return {
      key, label: def ? def.label[l] : key, value, unit: def?.unit ?? null, look,
      differs: planned !== undefined && planned !== null && planned !== value,
      stale: def?.kind === 'choice' ? !current : !def,
    }
  })
}

type Row = { id: string; job_id: string; confirmed_at: string; values: Readonly<Record<string, string | number>> }
const sameWords = (a: Row['values'], b: Row['values']): boolean => { const ka = Object.keys(a), kb = Object.keys(b); return ka.length === kb.length && ka.every((k) => Object.hasOwn(b, k) && a[k] === b[k]) }
/** The confirmations of one job, oldest first, one per distinct set of words: a crew of two who confirm the same thing show ONE line, and no person is named
 *  (nothing here ranks people, rule 7). */
export function confirmationsOfJob<R extends Row>(rows: readonly R[], jobId: string): R[] {
  const own = rows.filter((r) => r.job_id === jobId).sort((a, b) => (a.confirmed_at < b.confirmed_at ? -1 : a.confirmed_at > b.confirmed_at ? 1 : a.id < b.id ? -1 : 1))
  return own.filter((r, i) => own.findIndex((x) => sameWords(x.values, r.values)) === i)
}
